import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  SelectChangeEvent,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useFacilityLayoutData } from "@context";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import {
  buildTopLevelFacilityOptions,
  getFacilityHierarchyId,
  getSelectedFacilityFromSession,
  notifySelectedFacilitySessionUpdated,
  SELECTED_FACILITY_STORAGE_KEY,
  SELECTED_FACILITY_UPDATED_EVENT,
} from "@utils/facilityUtils";
import { isHoldingCompanyNetwork } from "@utils/networkFacilityRows";
import { getCurrentUserId } from "@utils/userUtils";
import { getSelectedCountryCode } from "@utils/countryUtils";
import {
  acceptIncomingChildLinkRequest,
  declineIncomingChildLinkRequest,
  listIncomingChildLinkRequests,
  type IncomingChildLinkRequestItem,
} from "@services/facilityNetworkLink";
import {
  getFacilityDashboard,
  normalizeFacilityDashboardResponse,
} from "@services/facilityDashboard";
import FacilityNetworkFacilityHeader from "./facilityNetwork/FacilityNetworkFacilityHeader";
import FacilityNetworkPageShell from "./facilityNetwork/FacilityNetworkPageShell";
import FacilityNetworkBackButton from "./facilityNetwork/FacilityNetworkBackButton";
import FacilityNetworkTitleRow from "./facilityNetwork/FacilityNetworkTitleRow";
import FacilityNetworkParentLinkRequestBanner from "./facilityNetwork/FacilityNetworkParentLinkRequestBanner";
import FacilityComplianceOverviewSection from "./facilityNetwork/FacilityComplianceOverviewSection";
import ParentLinkRequestDialog from "./facilityNetwork/ParentLinkRequestDialog";
import { mapIncomingLinkRequestToBanner } from "./facilityNetwork/parentLinkRequestUtils";
import { FN } from "./facilityNetwork/facilityNetworkTheme";

type NetworkFacility = MyFacilityListRow & { facility_id?: number };

function myFacilityRowToNetworkFacility(row: MyFacilityListRow): NetworkFacility {
  return { ...row, id: row.facility_id ?? row.id };
}

function findFacilityById(
  facilities: NetworkFacility[],
  facilityId: number,
): NetworkFacility | undefined {
  for (const facility of facilities) {
    const id = getFacilityHierarchyId(facility);
    if (id === facilityId) return facility;
    const child = (facility.child_facilities || []).find(
      (item) => getFacilityHierarchyId(item) === facilityId,
    ) as NetworkFacility | undefined;
    if (child) return child;
  }
  return facilities.find((f) => getFacilityHierarchyId(f) === facilityId);
}

function storeSelectedFacility(facility: NetworkFacility | undefined): void {
  if (typeof window === "undefined") return;
  if (!facility) {
    sessionStorage.removeItem(SELECTED_FACILITY_STORAGE_KEY);
    notifySelectedFacilitySessionUpdated();
    return;
  }
  sessionStorage.setItem(SELECTED_FACILITY_STORAGE_KEY, JSON.stringify(facility));
  notifySelectedFacilitySessionUpdated();
}

function formatAnalyzedDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function ReviewParentLinkRequestPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as {
    selectedFacility?: NetworkFacility;
    requestId?: number;
  } | null;
  const { myFacilities, loading: layoutLoading } = useFacilityLayoutData();
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | "">("");
  const [incomingRequests, setIncomingRequests] = useState<IncomingChildLinkRequestItem[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [complianceScore, setComplianceScore] = useState<number | null>(null);
  const [openItems, setOpenItems] = useState<number | null>(null);
  const [overdueCount, setOverdueCount] = useState<number | null>(null);
  const [lastAnalyzedLabel, setLastAnalyzedLabel] = useState<string | null>(null);

  const visibleFacilities = useMemo(
    () =>
      myFacilities
        .filter((row) => !isHoldingCompanyNetwork(row))
        .map(myFacilityRowToNetworkFacility),
    [myFacilities],
  );

  const groupedFacilityOptions = useMemo(
    () => buildTopLevelFacilityOptions(visibleFacilities),
    [visibleFacilities],
  );

  useEffect(() => {
    if (!visibleFacilities.length) {
      setSelectedFacilityId("");
      return;
    }
    const routeFacility = routeState?.selectedFacility;
    if (routeFacility) {
      const routeId = getFacilityHierarchyId(routeFacility);
      if (routeId != null && findFacilityById(visibleFacilities, routeId)) {
        setSelectedFacilityId(routeId);
        storeSelectedFacility(routeFacility);
        return;
      }
    }
    const stored = getSelectedFacilityFromSession<NetworkFacility>();
    if (stored) {
      const storedId = getFacilityHierarchyId(stored);
      if (storedId != null && findFacilityById(visibleFacilities, storedId)) {
        setSelectedFacilityId(storedId);
        return;
      }
    }
    const first = visibleFacilities[0];
    const firstId = first ? getFacilityHierarchyId(first) : "";
    setSelectedFacilityId(firstId ?? "");
  }, [visibleFacilities, routeState?.selectedFacility]);

  const selectedFacility = useMemo(
    () =>
      selectedFacilityId === ""
        ? undefined
        : findFacilityById(visibleFacilities, Number(selectedFacilityId)),
    [visibleFacilities, selectedFacilityId],
  );

  const activeRequest = useMemo(() => {
    const pending = incomingRequests.filter(
      (r) => r.status === "link_pending" || r.status === "pending",
    );
    if (!pending.length) return null;
    if (routeState?.requestId != null) {
      return pending.find((r) => r.id === routeState.requestId) ?? pending[0];
    }
    return pending[0];
  }, [incomingRequests, routeState?.requestId]);

  const bannerData = useMemo(
    () => (activeRequest ? mapIncomingLinkRequestToBanner(activeRequest) : null),
    [activeRequest],
  );

  const loadIncomingRequests = useCallback(async (facilityId: number) => {
    setRequestsLoading(true);
    setRequestsError(null);
    try {
      const items = await listIncomingChildLinkRequests(facilityId);
      setIncomingRequests(items);
    } catch (err) {
      setIncomingRequests([]);
      setRequestsError(err instanceof Error ? err.message : "Failed to load link requests.");
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedFacilityId === "") {
      setIncomingRequests([]);
      return;
    }
    loadIncomingRequests(Number(selectedFacilityId));
  }, [selectedFacilityId, loadIncomingRequests]);

  useEffect(() => {
    if (selectedFacilityId === "") return;
    const userId = Number(getCurrentUserId());
    if (!userId) return;

    let cancelled = false;
    setDashboardLoading(true);
    getFacilityDashboard(Number(selectedFacilityId), userId, getSelectedCountryCode())
      .then((res) => {
        if (cancelled) return;
        const normalized = normalizeFacilityDashboardResponse(res as unknown as Record<string, unknown>);
        setComplianceScore(normalized.profile_completeness?.completeness_percentage ?? null);
        setOpenItems(normalized.action_required?.length ?? 0);
        setOverdueCount(
          (normalized.action_required || []).filter((item) => item.urgency === "overdue").length +
            (normalized.calendar_strip?.events || []).filter((event) => event.status === "overdue")
              .length,
        );
        setLastAnalyzedLabel(
          formatAnalyzedDate(normalized.tiles?.analyses_run?.last_run_at) ??
            formatAnalyzedDate(normalized.tiles?.regulation_updates?.last_som_updated_at),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setComplianceScore(null);
          setOpenItems(null);
          setOverdueCount(null);
          setLastAnalyzedLabel(null);
        }
      })
      .finally(() => {
        if (!cancelled) setDashboardLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFacilityId]);

  const handleFacilityChange = (event: SelectChangeEvent<number | "">) => {
    const raw = event.target.value;
    if (raw === "") {
      setSelectedFacilityId("");
      storeSelectedFacility(undefined);
      return;
    }
    const facilityId = typeof raw === "number" ? raw : Number(raw);
    const facility = findFacilityById(visibleFacilities, facilityId);
    setSelectedFacilityId(facilityId);
    storeSelectedFacility(facility);
  };

  const navigateBackToNetwork = () => {
    navigate("/dashboard/facilities", {
      state: {
        currentView: "facility-network",
        selectedFacility,
      },
    });
  };

  const handleAccept = async () => {
    if (!activeRequest || selectedFacilityId === "") return;
    setSubmitting(true);
    setActionError(null);
    try {
      await acceptIncomingChildLinkRequest(Number(selectedFacilityId), activeRequest.id);
      setDialogOpen(false);
      navigateBackToNetwork();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to accept link request.");
      await loadIncomingRequests(Number(selectedFacilityId));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!activeRequest || selectedFacilityId === "") return;
    setSubmitting(true);
    setActionError(null);
    try {
      await declineIncomingChildLinkRequest(Number(selectedFacilityId), activeRequest.id);
      setDialogOpen(false);
      await loadIncomingRequests(Number(selectedFacilityId));
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to decline link request.");
      await loadIncomingRequests(Number(selectedFacilityId));
    } finally {
      setSubmitting(false);
    }
  };

  if (layoutLoading) {
    return (
      <FacilityNetworkPageShell>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240 }}>
          <CircularProgress sx={{ color: FN.primary }} />
        </Box>
      </FacilityNetworkPageShell>
    );
  }

  return (
    <FacilityNetworkPageShell>
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, width: "100%", pb: 0.75 }}>
        <FacilityNetworkFacilityHeader
          selectedFacilityId={selectedFacilityId}
          selectedFacility={selectedFacility}
          groupedFacilityOptions={groupedFacilityOptions}
          onFacilityChange={handleFacilityChange}
        />

        <Box
          sx={{
            mt: FN.headerToTabs,
            pt: 1,
            pb: FN.titleSeparatorPad,
            borderBottom: `1px solid ${FN.tabBarBorder}`,
            display: "flex",
            flexDirection: "column",
            gap: 1.625,
          }}
        >
          <FacilityNetworkBackButton onClick={navigateBackToNetwork} />
          <FacilityNetworkTitleRow title="Review requests" badge="" hideSeparator compactTop />
        </Box>

        <Box sx={{ pt: FN.contentAreaTopPad, display: "flex", flexDirection: "column", gap: 3 }}>
          {requestsLoading ? (
            <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} sx={{ color: FN.primary }} />
            </Box>
          ) : requestsError ? (
            <Alert severity="error">{requestsError}</Alert>
          ) : bannerData ? (
            <FacilityNetworkParentLinkRequestBanner
              request={bannerData}
              actionLabel="Review request"
              onAction={() => setDialogOpen(true)}
            />
          ) : (
            <Alert severity="info">No pending parent link requests for this facility.</Alert>
          )}

          {actionError ? <Alert severity="error">{actionError}</Alert> : null}

          <FacilityComplianceOverviewSection
            facilityName={selectedFacility?.facility_name || "Your facility"}
            lastAnalyzedLabel={lastAnalyzedLabel}
            complianceScore={complianceScore}
            openItems={openItems}
            overdueCount={overdueCount}
            loading={dashboardLoading}
          />
        </Box>
      </Box>

      <ParentLinkRequestDialog
        open={dialogOpen}
        request={bannerData}
        submitting={submitting}
        onClose={() => setDialogOpen(false)}
        onDecline={handleDecline}
        onAccept={handleAccept}
      />
    </FacilityNetworkPageShell>
  );
}
